import type { ShareableScopeKind } from "../../../../shared/rbac/permissions";
import {
  grantToRoleChip,
  type ResolveScopeName,
  type RoleChip,
} from "../../../lib/roleChip";
import { inferScopeKind } from "./inferScopeKind";

export type RoleInfo = { id: string; name: string; scopeKind: string };

type GrantInput = {
  id: string;
  scopeId: string;
  builtinRoleId: string | null;
  customRoleId: string | null;
};

export type AccessRow = {
  scopeId: string;
  scopeKind: ShareableScopeKind;
  name: string;
  chips: RoleChip[];
};

export type HiddenAccessCounts = {
  space: number;
  connected_app: number;
  skill: number;
  network: number;
  service_account: number;
};

const SCOPE_ORDER: Record<AccessRow["scopeKind"], number> = {
  space: 0,
  connected_app: 1,
  skill: 2,
  network: 3,
  service_account: 4,
};

export function buildAccessRows(
  grants: readonly GrantInput[],
  resolveScopeName: ResolveScopeName,
  rolesMap: ReadonlyMap<string, RoleInfo>
): { rows: AccessRow[]; hidden: HiddenAccessCounts } {
  const byScope = new Map<string, GrantInput[]>();
  for (const grant of grants) {
    const arr = byScope.get(grant.scopeId);
    if (arr) arr.push(grant);
    else byScope.set(grant.scopeId, [grant]);
  }

  const rows: AccessRow[] = [];
  const hidden: HiddenAccessCounts = {
    space: 0,
    connected_app: 0,
    skill: 0,
    network: 0,
    service_account: 0,
  };
  for (const [scopeId, scopeGrants] of byScope) {
    const scopeKind = inferScopeKind(scopeGrants[0]!, rolesMap);
    if (!scopeKind) continue;

    const name = resolveScopeName(scopeId, scopeKind) ?? null;
    if (name === null) {
      hidden[scopeKind]++;
      continue;
    }

    const chipMap = new Map<string, RoleChip>();
    for (const grant of scopeGrants) {
      const enriched = {
        ...grant,
        customRole: grant.customRoleId
          ? (rolesMap.get(grant.customRoleId) ?? null)
          : null,
      };
      const chip = grantToRoleChip(enriched);
      if (chip && !chipMap.has(chip.key)) chipMap.set(chip.key, chip);
    }

    rows.push({
      scopeId,
      scopeKind,
      name,
      chips: [...chipMap.values()],
    });
  }

  rows.sort((a, b) => {
    const kindDiff = SCOPE_ORDER[a.scopeKind] - SCOPE_ORDER[b.scopeKind];
    if (kindDiff !== 0) return kindDiff;
    return a.name.localeCompare(b.name);
  });

  return { rows, hidden };
}
